// src/components/usuarios/UserActionModals.tsx
// Modales de acciones sobre un usuario: se usan en el listado y en el detalle.

import React, { useEffect, useState } from "react";
import Modal from "../ui/Modal";
import { Alert } from "../ui/Alert";
import { userAdminApi } from "../../api/userAdminApi";
import { couponAdminApi } from "../../api/couponAdminApi";
import { PLAN_LABEL } from "../ui/Badges";
import { AdminCoupon, AdminUser, UserCoupon } from "../../types";
import { getErrorMessage } from "../../utils/errors";
import { formatDate, fullName, todayISO } from "../../utils/format";
import cuponImg from "../../assets/images/cupon.png";

interface ActionProps {
  user: AdminUser;
  onClose: () => void;
  /** Recibe el usuario actualizado y el mensaje de éxito del backend */
  onDone: (user: AdminUser, message: string) => void;
}

function userName(user: AdminUser) {
  return fullName(user.firstName, user.lastName, user.username);
}

// ── Activar cuenta ────────────────────────────────────────────────────────────
export function ActivateUserModal({ user, onClose, onDone }: ActionProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const confirm = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await userAdminApi.activar(user.id);
      onDone(res.content, res.message);
    } catch (err) {
      setError(getErrorMessage(err, "No se pudo activar la cuenta."));
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Activar cuenta"
      icon="bi-person-check-fill"
      iconColor="var(--tp-green)"
      onClose={onClose}
      busy={saving}
    >
      {error && <Alert type="error" message={error} />}
      <p style={{ fontSize: "0.9rem" }}>
        ¿Activar la cuenta de <strong>{userName(user)}</strong> ({user.email})?
      </p>
      <p className="text-muted-tp mb-0">
        El usuario podrá volver a iniciar sesión en la app Toinup.
      </p>
      {user.inactiveReason && (
        <p className="text-muted-tp mt-2 mb-0">
          Motivo de la desactivación: <em>{user.inactiveReason}</em>
        </p>
      )}
      <div className="modal-actions">
        <button className="btn-tp-ghost" onClick={onClose} disabled={saving}>
          Cancelar
        </button>
        <button className="btn-tp-success" onClick={confirm} disabled={saving}>
          {saving ? (
            <span className="spinner-border spinner-border-sm" />
          ) : (
            <i className="bi bi-check-lg" />
          )}
          Activar cuenta
        </button>
      </div>
    </Modal>
  );
}

// ── Desactivar cuenta ─────────────────────────────────────────────────────────
export function DeactivateUserModal({ user, onClose, onDone }: ActionProps) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError("Escribe el motivo de la desactivación.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await userAdminApi.desactivar(user.id, reason.trim());
      onDone(res.content, res.message);
    } catch (err) {
      setError(getErrorMessage(err, "No se pudo desactivar la cuenta."));
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Desactivar cuenta"
      icon="bi-person-x-fill"
      iconColor="var(--tp-red)"
      onClose={onClose}
      busy={saving}
    >
      <form onSubmit={confirm} noValidate>
        {error && <Alert type="error" message={error} />}
        <p style={{ fontSize: "0.9rem" }}>
          <strong>{userName(user)}</strong> ({user.email}) ya no podrá iniciar
          sesión en la app y se cerrarán sus sesiones abiertas.
        </p>
        <label className="form-label" htmlFor="deactivate-reason">
          Motivo
        </label>
        <textarea
          id="deactivate-reason"
          className="form-control"
          rows={3}
          maxLength={250}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Ej: Solicitud del usuario, uso indebido…"
          autoFocus
        />
        <div className="form-text">{reason.length}/250</div>
        <div className="modal-actions">
          <button
            type="button"
            className="btn-tp-ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>
          <button type="submit" className="btn-tp-danger" disabled={saving}>
            {saving ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <i className="bi bi-slash-circle" />
            )}
            Desactivar cuenta
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Asignar cupón ─────────────────────────────────────────────────────────────
export function AssignCouponModal({ user, onClose, onDone }: ActionProps) {
  const [coupons, setCoupons] = useState<AdminCoupon[]>([]);
  const [couponId, setCouponId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    couponAdminApi
      .asignables()
      .then((res) => {
        setCoupons(res.content);
        if (res.content.length === 1) setCouponId(res.content[0].id);
      })
      .catch((err) =>
        setError(getErrorMessage(err, "No se pudieron cargar los cupones.")),
      )
      .finally(() => setLoading(false));
  }, []);

  const selected = coupons.find((c) => c.id === couponId);

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!couponId) {
      setError("Selecciona el cupón a asignar.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await userAdminApi.asignarCupon(user.id, couponId);
      onDone(res.content, res.message);
    } catch (err) {
      setError(getErrorMessage(err, "No se pudo asignar el cupón."));
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Asignar cupón"
      icon="bi-ticket-perforated-fill"
      iconColor="var(--tp-morado)"
      onClose={onClose}
      busy={saving}
    >
      <form onSubmit={confirm} noValidate>
        {error && <Alert type="error" message={error} />}

        <div className="plan-highlight mb-3">
          <img src={cuponImg} alt="" />
          <div style={{ fontSize: "0.85rem" }}>
            <div style={{ fontWeight: 600, color: "var(--tp-primary)" }}>
              {userName(user)}
            </div>
            <div className="text-muted-tp">{user.email}</div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-3">
            <span
              className="spinner-border spinner-border-sm"
              style={{ color: "var(--tp-primary)" }}
            />
          </div>
        ) : coupons.length === 0 ? (
          <Alert
            type="info"
            message="No hay cupones del 100% vigentes y con usos disponibles. Crea uno en el menú Cupones."
          />
        ) : (
          <>
            <label className="form-label" htmlFor="assign-coupon">
              Cupón
            </label>
            <select
              id="assign-coupon"
              className="form-select"
              value={couponId}
              onChange={(e) => setCouponId(e.target.value)}
            >
              <option value="">Selecciona un cupón…</option>
              {coupons.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} · vence {formatDate(c.expirationDate)} ·{" "}
                  {c.quantityAvailable} usos disponibles
                </option>
              ))}
            </select>
            {selected && (
              <p className="text-muted-tp mt-2 mb-0">
                El usuario quedará con{" "}
                <strong>
                  Premium hasta el {formatDate(selected.expirationDate)}
                </strong>
                .{selected.description ? ` ${selected.description}` : ""}
              </p>
            )}
          </>
        )}

        <p className="form-text mt-3 mb-0">
          Solo se asignan cupones del 100%. Los cupones con descuento parcial se
          comparten por código y el usuario los aplica al pagar su suscripción
          en la app.
        </p>

        <div className="modal-actions">
          <button
            type="button"
            className="btn-tp-ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn-tp-morado"
            disabled={saving || loading || coupons.length === 0}
          >
            {saving ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <i className="bi bi-gift" />
            )}
            Asignar cupón
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Desasignar cupón ──────────────────────────────────────────────────────────
export function UnassignCouponModal({
  item,
  onClose,
  onDone,
}: {
  item: UserCoupon;
  onClose: () => void;
  onDone: (user: AdminUser, message: string) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const confirm = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await userAdminApi.desasignarCupon(
        item.userId,
        item.userCouponId,
      );
      onDone(
        res.content,
        `Cupón ${item.code} desasignado a ${item.fullName}. El uso se devolvió al cupón.`,
      );
    } catch (err) {
      setError(getErrorMessage(err, "No se pudo desasignar el cupón."));
      setSaving(false);
    }
  };

  const restoreLabel = item.restorePaymentStatus
    ? (PLAN_LABEL[item.restorePaymentStatus] ?? item.restorePaymentStatus)
    : "";
  const restoreHasDays =
    !!item.restoreExpirationDate && item.restoreExpirationDate >= todayISO();

  return (
    <Modal
      title="Desasignar cupón"
      icon="bi-x-octagon-fill"
      iconColor="var(--tp-red)"
      onClose={onClose}
      busy={saving}
    >
      {error && <Alert type="error" message={error} />}
      <p style={{ fontSize: "0.9rem" }}>
        ¿Quitar el cupón <span className="code-chip">{item.code}</span> a{" "}
        <strong>{item.fullName}</strong> ({item.email})?
      </p>

      {item.inEffect ? (
        <div className="tp-alert info mb-0">
          <i className="bi bi-info-circle-fill" />
          <span>
            Este cupón es el que hoy le da el Premium. Al quitarlo su
            suscripción queda en <strong>{restoreLabel}</strong>
            {restoreHasDays
              ? ` hasta el ${formatDate(item.restoreExpirationDate)}, como estaba antes de asignarle el cupón.`
              : " sin días vigentes, y la app le ofrecerá comprar la suscripción."}
          </span>
        </div>
      ) : (
        <p className="text-muted-tp mb-0">
          El cupón ya no está vigente en su suscripción, así que solo se elimina
          el registro. La suscripción no cambia.
        </p>
      )}
      <p className="text-muted-tp mt-2 mb-0">
        El uso se devuelve al cupón para que se pueda volver a asignar.
      </p>

      <div className="modal-actions">
        <button className="btn-tp-ghost" onClick={onClose} disabled={saving}>
          Cancelar
        </button>
        <button className="btn-tp-danger" onClick={confirm} disabled={saving}>
          {saving ? (
            <span className="spinner-border spinner-border-sm" />
          ) : (
            <i className="bi bi-x-lg" />
          )}
          Desasignar cupón
        </button>
      </div>
    </Modal>
  );
}

export type UserAction = "activate" | "deactivate" | "coupon";

/** Renderiza el modal que corresponde a la acción elegida. */
export function UserActionModal({
  action,
  ...props
}: ActionProps & { action: UserAction }) {
  if (action === "activate") return <ActivateUserModal {...props} />;
  if (action === "deactivate") return <DeactivateUserModal {...props} />;
  return <AssignCouponModal {...props} />;
}
